<app-loading-overlay [isLoading]="profServ.sub_processing" [fullscreen]="true" class="dialog-fill">
  <bv-dialog-title>{{ plan.name }}</bv-dialog-title>

  <bv-dialog-body>
    <p class="checkout-subtitle">
      {{
      (profServ.latestProfile?.subInterval == "annual"
      ? "ANNUAL_BILLING"
      : "MONTHLY_BILLING"
      ) | translate
      }}
    </p>

    <p class="checkout-renews text-sm text-gray-400">{{ "RENEWS_CANCEL" | translate }}</p>

    @if (showRussianCardWarning) {
      <bv-alert variant="warning">
        {{ "RUSSIAN_CARD_WARNING" | translate }}
      </bv-alert>
    }

    <!-- Stripe card input -->
    @if (
      !profServ.latestProfile?.stripeCards ||
      profServ.latestProfile?.subscription_card_declined
      ) {
      <div class="mt-4">
        <app-credit-card></app-credit-card>
      </div>
    }

    <!-- Coupon code -->
    <div class="coupon-row">
      <input
        bv-input
        class="coupon-field"
        type="text"
        placeholder="{{ 'COUPON_CODE' | translate }}"
        [(ngModel)]="couponID"
        name="couponID"
        />
      <button
        id="btn_checkout_apply_coupon"
        bv-button variant="secondary"
        (click)="applyCoupon()"
        [disabled]="!couponID"
        class="coupon-btn"
        >
        {{ "APPLY" | translate }}
      </button>
    </div>

    <!-- Currency-change explainer (above the summary) -->
    @if (isCurrencyChange) {
      <p class="checkout-note text-sm text-gray-300">
        {{ "CURRENCY_CHANGE_EXPLAINER" | translate }}
      </p>
    }

    <!-- Order summary -->
    <div class="order-summary">
      <!-- Cancellation line (currency change only — old sub gets cancelled) -->
      @if (isCurrencyChange) {
        <div class="summary-line">
          <span class="summary-label">{{ "CANCELLATION" | translate }}</span>
          <span class="summary-desc">{{
            configServ.getPlanName(profServ.latestSubscription?.code)
          }}</span>
          <span class="summary-amount"></span>
        </div>
      }

      <!-- Refund line (currency change only — refund is in OLD currency) -->
      @if (isCurrencyChange && conversionPreview?.refund?.amount) {
        <div class="summary-line">
          <span class="summary-label">{{ "REFUND" | translate }}</span>
          <span class="summary-desc">{{ "REFUND_TO_CARD" | translate }}</span>
          <span class="summary-amount">-{{
            conversionPreview!.refund.amount / 100
            | currency : conversionPreview!.refund.currency : "symbol-narrow"
          }}</span>
        </div>
      }

      <!-- New subscription line (currency change only — labelled "new") -->
      @if (isCurrencyChange) {
        <div class="summary-line">
          <span class="summary-label">{{ "NEW_SUBSCRIPTION" | translate }}</span>
          <span class="summary-desc">
            {{ plan.name }} - 1
            @if (profServ.latestProfile?.subInterval == 'annual') {
              <span>{{ "YEAR" | translate }}</span>
            }
            @if (profServ.latestProfile?.subInterval == 'monthly') {
              <span>{{ "MONTH" | translate }}</span>
            }
          </span>
          <span class="summary-amount">{{
            getCost() | currency : profServ.latestProfile?.currency : "symbol-narrow"
          }}</span>
        </div>
      }

      <!-- Subscription line -->
      @if (!isCurrencyChange && (!subServ.latestProration || !subServ.latestProration?.remaining)) {
        <div class="summary-line">
          <span class="summary-label">{{ "SUBSCRIPTION" | translate }}</span>
          <span class="summary-desc">
            {{ plan.name }} - 1
            @if (profServ.latestProfile?.subInterval == 'annual') {
              <span>{{ "YEAR" | translate }}</span>
            }
            @if (profServ.latestProfile?.subInterval == 'monthly') {
              <span>{{ "MONTH" | translate }}</span>
            }
          </span>
          <span class="summary-amount">{{
            getCost() | currency : profServ.latestProfile?.currency : "symbol-narrow"
          }}</span>
        </div>
      }

      <!-- Prorated subscription line -->
      @if (subServ.latestProration && subServ.latestProration?.remaining) {
        <div class="summary-line">
          <span class="summary-label">{{ "PRORATED_SUBSCRIPTION" | translate }}</span>
          <span class="summary-desc">
            {{ plan.name }} - 1
            @if (profServ.latestProfile?.subInterval == 'annual') {
              <span>{{ "YEAR" | translate }}</span>
            }
            @if (profServ.latestProfile?.subInterval == 'monthly') {
              <span>{{ "MONTH" | translate }}</span>
            }
            @if (profServ.latestSubscription?.discount?.coupon?.valid) {
              <span
              >({{ profServ.latestSubscription?.discount?.coupon?.id }})</span>
            }
          </span>
          <span class="summary-amount">{{
            getRemaining() | currency : profServ.latestProfile?.currency : "symbol-narrow"
          }}</span>
        </div>
      }

      <!-- Credit line -->
      @if (subServ.latestProration && subServ.latestProration?.credit) {
        <div class="summary-line">
          <span class="summary-label">{{ "CREDIT" | translate }}</span>
          <span class="summary-desc">{{ profServ.latestSubscription?.plan.name }}</span>
          <span class="summary-amount">{{
            getCredit() | currency : profServ.latestProfile?.currency : "symbol-narrow"
          }}</span>
        </div>
      }

      <!-- Discount line -->
      @if (coupon && discount) {
        <div class="summary-line">
          <span class="summary-label">{{ coupon.id }}</span>
          <span class="summary-desc">
            @if (coupon.percent_off) {
              <span>-{{ coupon.percent_off }}% </span>
            }
            @if (coupon.duration_in_months && profServ.latestProfile?.subInterval == 'monthly') {
              <span>
                {{ coupon.duration_in_months }} {{ "MONTHS" | translate }}
              </span>
            }
          </span>
          <span class="summary-amount">{{
            discount | currency : profServ.latestProfile?.currency : "symbol-narrow"
          }}</span>
        </div>
      }

      <!-- Sub-total line -->
      @if (subTotal) {
        <div class="summary-line">
          <span class="summary-label">{{ "SUBTOTAL" | translate }}</span>
          <span class="summary-desc"></span>
          <span class="summary-amount">{{
            subTotal | currency : profServ.latestProfile?.currency : "symbol-narrow"
          }}</span>
        </div>
      }

      <!-- VAT line -->
      @if (profServ.latestProfile?.vat && tax) {
        <div class="summary-line">
          <span class="summary-label">{{ "VAT" | translate }} ({{ profServ.latestProfile?.vat }}%)</span>
          <span class="summary-desc"></span>
          <span class="summary-amount">{{
            tax | currency : profServ.latestProfile?.currency : "symbol-narrow"
          }}</span>
        </div>
      }

      <!-- Total line (when account balance exists) -->
      @if (acctBal) {
        <div class="summary-line">
          <span class="summary-label">{{ "TOTAL" | translate }}</span>
          <span class="summary-desc"></span>
          <span class="summary-amount">{{
            total | currency : profServ.latestProfile?.currency : "symbol-narrow"
          }}</span>
        </div>
      }

      <!-- Account balance line -->
      @if (acctBal) {
        <div class="summary-line">
          <span class="summary-label">{{ "ACCOUNT_BALANCE" | translate }}</span>
          <span class="summary-desc"></span>
          <span class="summary-amount">{{
            acctBal | currency : profServ.latestProfile?.currency : "symbol-narrow"
          }}</span>
        </div>
      }

      <!-- Amount due / Total (final) -->
      <div class="summary-line summary-line--total">
        @if (!acctBal) {
          <span class="summary-label">{{ "TOTAL" | translate }}</span>
        }
        @if (acctBal) {
          <span class="summary-label">{{ "AMOUNT_DUE" | translate }}</span>
        }
        <span class="summary-desc"></span>
        <span class="summary-amount summary-amount--total">{{
          profServ.amountDue | currency : profServ.latestProfile?.currency : "symbol-narrow"
        }}</span>
      </div>
    </div>

    <!-- Refund timing footnote (currency change only) -->
    @if (isCurrencyChange) {
      <p class="checkout-note text-sm text-gray-400">
        {{ "REFUND_TIMING_NOTE" | translate }}
      </p>
    }

    <!-- Account credit notice -->
    @if (profServ.amountDue <= 0) {
      <div class="credit-notice">
        {{ "YOUR_ACCOUNT_CREDITED" | translate }}:
        {{ -profServ.amountDue | currency : profServ.latestProfile?.currency : "symbol-narrow" }}
      </div>
    }

    <!-- Stripe badge -->
    <div class="stripe-badge">
      <a href="http://stripe.com" target="_ext">
        <img src="/assets/img/stripe.png" alt="Powered by Stripe" />
      </a>
    </div>

    <!-- Terms & Conditions acceptance -->
    <div class="checkout-terms">
      <bv-checkbox
        id="checkout-terms-accept"
        name="termsAccepted"
        [(ngModel)]="termsAccepted"
        class="checkout-terms-checkbox"
        >
        <span class="checkout-terms-text">
          {{ "I_AGREE_TO_THE" | translate }}
          <a
            [href]="termsUrl"
            target="_blank"
            rel="noopener"
            (click)="$event.stopPropagation()"
          >{{ "TERMS_AND_CONDITIONS" | translate }}</a>
          {{ "AND" | translate }}
          <a
            [href]="privacyUrl"
            target="_blank"
            rel="noopener"
            (click)="$event.stopPropagation()"
          >{{ "ADMIN_PRIVACY_POLICY" | translate }}</a>
        </span>
      </bv-checkbox>
    </div>

  </bv-dialog-body>

  <!-- Actions -->
  <bv-dialog-footer>
    @if (profServ.amountDue > 0) {
      <button
        id="btn_checkout_pay"
        bv-button variant="primary"
        (click)="subscribe()"
        [disabled]="!termsAccepted"
        class="checkout-pay-btn"
        >
        {{ "PAY" | translate }}
        {{ profServ.amountDue | currency : profServ.latestProfile?.currency : "symbol-narrow" }}
      </button>
    }
    @if (profServ.amountDue <= 0) {
      <button
        id="btn_checkout_confirm_subscribe"
        bv-button variant="primary"
        (click)="subscribe()"
        [disabled]="!termsAccepted"
        class="checkout-pay-btn"
        >
        {{ "CONFIRM" | translate }}
      </button>
    }
    <app-close-dialog-button></app-close-dialog-button>
  </bv-dialog-footer>
</app-loading-overlay>
